import type React from "react";
import { Fragment, useMemo, useState } from "react";
import { KeyboardArrowDown, KeyboardArrowUp, MoreVert as MoreVertIcon } from "@mui/icons-material";
import cx from "classnames";
import { Button, IconButton, Menu, MenuItem, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useConfirm } from "material-ui-confirm";
import styled from "styled-components";

import { type CaseBase, CaseCopyStatus, CaseStatus } from "src/types";
import { useCaseDeleteMutation } from "src/hooks";
import { isDisabled, isEnabled } from "src/utils/permission.utils";
import { match, parse } from "../utils/autosuggest-highlight.utils";
import { CaseStatusChip } from "src/components/CaseStatusChip";
import { CaseErrorsDialog } from "src/components/CaseErrorsDialog";
import { StickyTable } from "src/components/StickyTable";
import { IsVisible } from "src/components/permissions";
import { TableHeadCell } from "src/components/TableHeadCell";
import { CaseCopyDialog } from "src/components/CaseCopyDialog";
import { CaseCopyStatusChip } from "src/components/CaseCopyStatusChip";

interface Props {
  className?: string;
  data: CaseBase[];
  searchQuery: string;
}

export type OrderColumn = "name" | "dateCreated" | "status";
export type OrderDirection = "asc" | "desc";

export function CaseOverviewTable(props: Props) {
  const { t } = useTranslation();
  const { data: cases } = props;
  console.log("Cases: ", cases);
  /**
   * Local state
   */
  const [orderBy, setOrderBy] = useState<OrderColumn>("dateCreated");
  const [orderDirection, setOrderDirection] = useState<OrderDirection>("desc");
  /**
   * Memo
   */
  const visualData = useMemo(() => {
    const filteredData = filterByQuery(cases, props.searchQuery);
    const sortedData = sortData(filteredData, orderBy, orderDirection);
    return sortedData;
  }, [cases, orderBy, orderDirection, props.searchQuery]);

  /**
   * Functions
   */
  function handleSort(column: OrderColumn) {
    if (column !== orderBy) {
      setOrderBy(column);
      setOrderDirection("asc");
    }
    if (column === orderBy) {
      toggleOrderDirection();
    }
  }

  function toggleOrderDirection() {
    if (orderDirection === "asc") {
      setOrderDirection("desc");
    } else if (orderDirection === "desc") {
      setOrderDirection("asc");
    }
  }
  return render();

  function render() {
    return (
      <StyledStickyTable className={props.className}>
        <TableHead>
          <TableRow>
            {/* <TableCell /> */}
            <TableHeadCell<OrderColumn>
              dataKey="name"
              orderBy={orderBy}
              orderDirection={orderDirection}
              onSort={() => handleSort("name")}
            >
              {t("label.caseName")}
            </TableHeadCell>
            <TableHeadCell<OrderColumn>
              dataKey="dateCreated"
              orderBy={orderBy}
              orderDirection={orderDirection}
              onSort={() => handleSort("dateCreated")}
            >
              {t("label.created")}
            </TableHeadCell>
            <TableHeadCell<OrderColumn>
              dataKey="status"
              orderBy={orderBy}
              orderDirection={orderDirection}
              onSort={() => handleSort("status")}
            >
              {t("label.status")}
            </TableHeadCell>
            {/* <TableHeadCell<OrderColumn>
              dataKey="year"
              orderBy={orderBy}
              orderDirection={orderDirection}
              onSort={() => handleSort("year")}
            >
              {t("label.status")}
            </TableHeadCell> */}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {visualData.map((item, index) => (
            <Row
              key={item.id}
              data={item}
              className={index % 2 === 0 ? "even" : "odd"}
              searchQuery={props.searchQuery}
            />
          ))}
        </TableBody>
      </StyledStickyTable>
    );
  }
}

function Row(props: { data: CaseBase; searchQuery: string; className?: string }) {
  const { data } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [openCaseCopyDialog, setOpenCaseCopyDialog] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const deleteMutation = useCaseDeleteMutation(data.id);

  function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }

  /**
   * Functions
   */
  function handleOpenCase() {
    if (isEnabled(data.permissions.update)) {
      navigate(`/cases/${data.id}`);
    }
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleDelete() {
    confirm({
      confirmationButtonProps: { autoFocus: true },
      title: t("label.areYouSure"),
      description: t("confirm.deleteCase", { name: data.name }),
    })
      .then(() => deleteMutation.mutate())
      .finally(() => handleClose());
  }

  return render();

  function render() {
    const matches = match(data.name, props.searchQuery, true);
    const parts = parse(data.name, matches);
    const showCopyStatus = data.copyStatus === CaseCopyStatus.COPYING || data.copyStatus === CaseCopyStatus.FAILED;

    return (
      <Fragment key={data.id}>
        <TableRow className={`${props.className} ${showDetails ? "details-open" : ""}`.trim()}>
          {/* <TableCell className="fixed-width">
            <IconButton onClick={() => setShowDetails(!showDetails)}>
              {showDetails ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell> */}
          <TableCell scope="row" className="case-name-cell">
            <Button
              variant="text"
              className={cx("name", {
                editable: isEnabled(data.permissions.update),
              })}
              disabled={isDisabled(data.permissions.update)}
              onClick={handleOpenCase}
            >
              {parts.map((part, index) => (
                <span
                  key={index}
                  className={cx("part", {
                    highlight: part.highlight,
                  })}
                >
                  {part.text}
                </span>
              ))}
            </Button>
            <span className="scenarios">{t("label.scenariosConfigured", { count: data.nbScenarios })}</span>
          </TableCell>
          <TableCell>{t("label.dateCreated", { date: data.dateCreated })}</TableCell>
          <TableCell>
            {showCopyStatus && <CaseCopyStatusChip label={t(`label.${data.copyStatus}`)} status={data.copyStatus} />}
            {!showCopyStatus && <CaseStatusChip label={t(`label.caseStatus.${data.status}`)} status={data.status} />}
          </TableCell>
          <TableCell align="right">{renderRowActions()}</TableCell>
        </TableRow>
        {/* {showDetails && <CaseOverviewDetailsTable caseId={data.id} className={props.className} />} */}
      </Fragment>
    );
  }

  function renderRowActions() {
    return (
      <>
        {data.status === CaseStatus.CALCULATION_FINISHED && (
          <Button variant="text" color="primary" component={NavLink} to={`/cases/${data.id}/results`}>
            {t("label.viewResults")}
          </Button>
        )}
        {(data.status === CaseStatus.CREATION_FAILED ||
          data.status === CaseStatus.CALCULATION_FAILED ||
          data.copyStatus === CaseCopyStatus.FAILED) && (
          <>
            <Button className="btn-error" variant="text" onClick={() => setShowErrors(true)}>
              {t("label.errorDetails")}
            </Button>
            <CaseErrorsDialog open={showErrors} case={data} onClose={() => setShowErrors(false)} />
          </>
        )}
        <IconButton onClick={handleClick} size="large">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleClose()}>
          <MenuItem disabled={isDisabled(data.permissions.update)} onClick={handleOpenCase}>
            {t("label.edit")}
          </MenuItem>
          <MenuItem
            disabled={isDisabled(data.permissions.update)}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              setOpenCaseCopyDialog(true);
            }}
          >
            {t("label.copy")}
          </MenuItem>
          <IsVisible permission={data.permissions.delete}>
            <MenuItem
              disabled={isDisabled(data.permissions.delete)}
              onClick={(event) => {
                event.stopPropagation();
                handleDelete();
              }}
            >
              {t("label.delete")}
            </MenuItem>
          </IsVisible>
        </Menu>
        <CaseCopyDialog onClose={() => setOpenCaseCopyDialog(false)} caseId={data.id} open={openCaseCopyDialog} />
      </>
    );
  }
}

const StyledStickyTable = styled(StickyTable)`
  .fixed-width {
    width: 70px;
  }
  .details-open {
    .MuiTableCell-root {
      border-bottom: unset;
    }
  }
  .case-name-cell {
    display: flex;
    flex-direction: column;

    .name {
      text-transform: none;
      justify-content: flex-start;
      color: ${({ theme }) => theme.palette.text.secondary};
      margin-left: ${({ theme }) => theme.spacing(-1)};

      &.editable {
        color: ${({ theme }) => theme.palette.primary.main};
      }
    }

    .part {
      font-weight: 500;

      &.highlight {
        font-weight: 900;
      }
    }

    .scenarios {
      font-style: italic;
      font-size: 11px;
      font-weight: 400;
    }
  }

  .btn-error {
    color: ${({ theme }) => theme.palette.error.main};
  }
`;

function sortData(data: CaseBase[], column: OrderColumn, direction: OrderDirection) {
  if (column === "dateCreated") {
    return sortByDate(data, column, direction);
  } else {
    return sortByString(data, column, direction);
  }
}

function sortByDate(data: CaseBase[], column: OrderColumn, direction: OrderDirection) {
  return [...data].sort((a, b) => {
    const nameA = (a[column] as Date).getTime(); // ignore upper and lowercase
    const nameB = (b[column] as Date).getTime(); // ignore upper and lowercase
    if (direction === "asc") {
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
    }
    if (direction === "desc") {
      if (nameA > nameB) return -1;
      if (nameA < nameB) return 1;
    }

    // names must be equal
    return 0;
  });
}

function sortByString(data: CaseBase[], column: OrderColumn, direction: OrderDirection) {
  return [...data].sort((a, b) => {
    const nameA = a[column].toString().toUpperCase(); // ignore upper and lowercase
    const nameB = b[column].toString().toUpperCase(); // ignore upper and lowercase
    if (direction === "asc") {
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
    }
    if (direction === "desc") {
      if (nameA > nameB) return -1;
      if (nameA < nameB) return 1;
    }

    // names must be equal
    return 0;
  });
}

function filterByQuery(cases: CaseBase[], query: string) {
  return cases.filter((acase) => {
    return acase.name.toLowerCase().includes(query.toLowerCase());
  });
}
